import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { PricingPreviewDocument } from "@/generated/graphql";
import {
  pricingModeToGraphql,
  resolvePercentValue,
  type StorePricingMode,
} from "@/lib/pricing";

export interface PricingPreviewParams {
  effectiveCostCents: number;
  pricingMode?: StorePricingMode | null;
  markupPercent: string;
  targetNetMarginPercent: string;
  taxPercent: string;
  storeDefaultMarkupPercent?: number | null;
  storeDefaultTargetNetMarginPercent?: number | null;
  storeDefaultTaxPercent?: number | null;
}

export function usePricingPreview(params: PricingPreviewParams | null) {
  const [debouncedParams, setDebouncedParams] = useState(params);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedParams(params), 300);
    return () => window.clearTimeout(timer);
  }, [params]);

  const input = useMemo(() => {
    if (!debouncedParams || debouncedParams.effectiveCostCents <= 0) return null;

    return {
      effectiveCostCents: debouncedParams.effectiveCostCents,
      pricingMode: pricingModeToGraphql(
        debouncedParams.pricingMode ?? "target_net_margin"
      ),
      markupPercent: resolvePercentValue(
        debouncedParams.markupPercent,
        debouncedParams.storeDefaultMarkupPercent
      ),
      targetNetMarginPercent: resolvePercentValue(
        debouncedParams.targetNetMarginPercent,
        debouncedParams.storeDefaultTargetNetMarginPercent
      ),
      taxPercent: resolvePercentValue(
        debouncedParams.taxPercent,
        debouncedParams.storeDefaultTaxPercent
      ),
    };
  }, [debouncedParams]);

  const { data, previousData, loading } = useQuery(PricingPreviewDocument, {
    variables: { input: input! },
    skip: input == null,
    fetchPolicy: "cache-and-network",
  });

  const breakdown = data?.pricingPreview ?? previousData?.pricingPreview ?? null;

  return {
    breakdown,
    loading: loading && breakdown == null,
  };
}
